import { EVENT_STATUS } from '@hedepy/common/enums/EVENT'
import type { Event } from '@hedepy/common/schemas/modules/event/event'
import { useRouter } from 'next/router'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { createSelector } from 'reselect'

import { useAuthenticatedUser } from '@/legacy-src/hooks/useAuthenticatedUser'
import { type ReduxState } from '@/legacy-src/store/reducers'

import { useApis } from '../../contexts/api-context'
import { useUserIp } from '../../contexts/user-ip-context'
import UserActions, { USER_TYPE } from '../../redux/UserRedux'
import { useAppSelector } from '../../store/hooks'
import { fetchingSelector } from '../../store/selectors/GeneralSelectors'
import { handleError } from '../../utilities/handlerError'
import toaster from '../../utilities/toaster'
import useCompanyBenefits from '../useCompanyBenefits'
import useEvents from '../useEvents'
import { type HandleReserveEventOptions } from './interface'
import { _handleReserveEvent, createUsedEventsMap } from './useEventActions.utils'

export type UsedEventsMap = { [userSubscriptionId: number]: { [monthYear: string]: number } }

const therapistEventsFilter = (state: ReduxState) => state.therapist.appointments
const clientEventsFilter = (state: ReduxState) => state.client.appointments

const flatClientEventsSelector = createSelector(
  [therapistEventsFilter, clientEventsFilter, (_: ReduxState, { type }: { type: number }) => type],
  (therapistEvents, clientEvents, userType) => {
    if (userType === USER_TYPE.THERAPIST) return therapistEvents ?? []

    const therapistIds = Object.keys(clientEvents) as unknown as number[]

    return therapistIds.reduce<Event[]>((acc, cur) => [...acc, ...(clientEvents[cur] ?? [])], [])
  },
)
const usedSubscriptionEventsSelector = createSelector(flatClientEventsSelector, (events) => createUsedEventsMap(events))

const useEventActions = () => {
  const dispatch = useDispatch()
  const { api } = useApis()
  const { user, isClient } = useAuthenticatedUser()
  const { userIp } = useUserIp()
  const router = useRouter()
  const { refetchQuota } = useCompanyBenefits()
  const { refetchEvents } = useEvents(isClient ? 'all' : 'current')

  const events = useAppSelector((state) =>
    flatClientEventsSelector(state, {
      type: user.type,
    }),
  )

  const usedSubscriptionEvents = useAppSelector((state) =>
    usedSubscriptionEventsSelector(state, {
      type: user.type,
    }),
  )

  const reservedEventsCount = events.filter((event) =>
    [EVENT_STATUS.RESERVED, EVENT_STATUS.COMPLETED, EVENT_STATUS.PRE_BOOKING, EVENT_STATUS.CANCELED_BY_CLIENT].includes(
      event.status,
    ),
  ).length

  const isLoading = useAppSelector((state) => fetchingSelector(state, 'allAppointments'))

  const onReserveEvent = useCallback(
    (props: HandleReserveEventOptions) =>
      _handleReserveEvent(props, api, user.id, dispatch, router, userIp, refetchEvents, refetchQuota, user),
    [api, user, dispatch, router, userIp],
  )

  const onCancelEvent = useCallback(
    async ({
      eventId,
      shouldRedirect,
      therapistId,
    }: {
      eventId: number
      therapistId?: number
      shouldRedirect?: boolean
      startDate?: string
    }) => {
      try {
        await api.cancelEvent(eventId)
        if (therapistId && shouldRedirect) {
          router.push({ pathname: `/client/${therapistId}/profile` })
        }
        refetchQuota()
        dispatch(UserActions.getAllEventsRequest(therapistId ? USER_TYPE.CLIENT : USER_TYPE.THERAPIST))
        dispatch(UserActions.onChangeUserValue('cancelModalVisible', false))
        refetchEvents((events) => events?.filter((event) => event.id !== eventId))
        toaster.success('component.toaster.cancelAppointmentSuccess')
      } catch (e) {
        console.log(e)
        handleError('cancelAppointment', e)
      }
    },
    [dispatch, refetchQuota],
  )

  const refetchReduxEvents = useCallback(() => dispatch(UserActions.getAllEventsRequest(user.type)), [user.type])

  return {
    // the events here are clashing with useEvents
    // ideally this should be removed or the two should be merged
    events,
    usedSubscriptionEvents,
    isLoadingEvents: isLoading,
    onReserveEvent,
    onCancelEvent,
    reservedEventsCount,
    refetchReduxEvents,
  }
}

export default useEventActions
